import { CustomDataGrid } from "@app/components";
import { Box, Grid, TextField, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { GridColDef } from "@mui/x-data-grid";

export enum LABELS_TABLE_CALCINATION {
  TARA = "Tara cápsula (g)",
  PESO_CALCINADO = "Peso calcinado muestra + cápsula (g)",
  PESO_CALCINADO_MUESTRA = "Peso calcinado muestra (g)",
}

interface AnalysisFormProps {
  count: number;
  setCalcinationValue: (value: number) => void;
}

const TableCalcinationForm = ({
  count,
  setCalcinationValue,
}: AnalysisFormProps) => {
  const { watch, setValue } = useFormContext();
  let rowsCurva = watch("rowsCurvaCalcination");
  const inputRef = useRef<{ [key: string]: HTMLInputElement | null }>({});

  if (!rowsCurva || !Array.isArray(rowsCurva)) {
    rowsCurva = [];
  }

  useEffect(() => {
    if (!rowsCurva || rowsCurva.length === 0) {
      const initialRows = generateInitialRows(count);
      setValue("rowsCurvaCalcination", initialRows);
    }
  }, [rowsCurva, setValue, count]);

  const generateInitialRows = (count: number) => {
    const rows = [
      {
        id: "calcinacion_1",
        label: LABELS_TABLE_CALCINATION.TARA,
        ...Array.from({ length: count }, (_, i) => ({
          [`capsule_${i + 1}`]: "",
        })).reduce((acc, val, i) => ({ ...acc, [`capsule_${i + 1}`]: "" }), {}),
        total: 0,
      },
      {
        id: "calcinacion_2",
        label: LABELS_TABLE_CALCINATION.PESO_CALCINADO,
        ...Array.from({ length: count }, (_, i) => ({
          [`capsule_${i + 1}`]: "",
        })).reduce((acc, val, i) => ({ ...acc, [`capsule_${i + 1}`]: "" }), {}),
        total: 0,
      },
      {
        id: "calcinacion_3",
        label: LABELS_TABLE_CALCINATION.PESO_CALCINADO_MUESTRA,
        ...Array.from({ length: count }, (_, i) => ({
          [`capsule_${i + 1}`]: "",
        })).reduce((acc, val, i) => ({ ...acc, [`capsule_${i + 1}`]: "" }), {}),
        total: 0,
      },
    ];
    return rows;
  };

  const columnsCalcinationCurva: GridColDef[] = useMemo(() => {
    const dynamicColumns: GridColDef[] = [
      {
        field: "label",
        headerName: "Calcinación",
        headerAlign: "center",
        align: "left",
        flex: 1,
        editable: false,
      },
    ];

    for (let i = 1; i <= count; i++) {
      dynamicColumns.push({
        field: `capsule_${i}`,
        headerName: `Cápsula ${i}`,
        headerAlign: "center",
        align: "center",
        editable: true,
        flex: 1,
        renderCell: (params) => {
          return (
            <Box
              sx={{
                width: "100%",
                display: "flex",
              }}
            >
              <TextField
                inputRef={(el) =>
                  (inputRef.current[`${params.id}_${params.field}`] = el)
                }
                type="text"
                value={params.value}
                disabled={
                  params.row.label ===
                  LABELS_TABLE_CALCINATION.PESO_CALCINADO_MUESTRA
                }
                onChange={(e) => {
                  if (
                    params.row.label !==
                    LABELS_TABLE_CALCINATION.PESO_CALCINADO_MUESTRA
                  ) {
                    setValue(
                      `rowsCurvaCalcination.${params.id}.${params.field}`,
                      e.target.value
                    );
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Tab") {
                    e.preventDefault();

                    const keys = Object.keys(inputRef.current);
                    const currentIndex = keys.indexOf(
                      `${params.id}_${params.field}`
                    );

                    if (currentIndex !== -1) {
                      let nextIndex = currentIndex + 1;
                      while (
                        nextIndex < keys.length &&
                        inputRef.current[keys[nextIndex]]?.disabled
                      ) {
                        nextIndex++;
                      }

                      const nextInputKey = keys[nextIndex];

                      if (nextInputKey && inputRef.current[nextInputKey]) {
                        setTimeout(
                          () => inputRef.current[nextInputKey]?.focus(),
                          0
                        );
                      }
                    }
                  }
                }}
                fullWidth
                sx={{
                  "& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
                    boxSizing: "inherit",
                  },
                }}
              />
            </Box>
          );
        },
      });
    }

    dynamicColumns.push({
      field: "total",
      headerName: "Total",
      headerAlign: "center",
      align: "center",
      editable: false,
      flex: 1,
      renderCell: (params) => {
        const total = Number(params.row.total || 0);
        return <div style={{ fontWeight: "bold" }}>{total.toFixed(2)}</div>;
      },
    });

    return dynamicColumns;
  }, [count]); // eslint-disable-line react-hooks/exhaustive-deps

  const processRowUpdate = (newRow) => {
    let updatedRows = rowsCurva.map((row) => ({ ...row }));

    if (
      newRow.label === LABELS_TABLE_CALCINATION.TARA ||
      newRow.label === LABELS_TABLE_CALCINATION.PESO_CALCINADO
    ) {
      let total = 0;

      for (let i = 1; i <= count; i++) {
        const capsuleValue = Number(newRow[`capsule_${i}`]) || 0;
        total += capsuleValue;
      }

      newRow.total = total.toFixed(2);
    }

    updatedRows = updatedRows.map((row) =>
      row.id === newRow.id ? newRow : row
    );

    const taraRow = updatedRows.find(
      (row) => row.label === LABELS_TABLE_CALCINATION.TARA
    );
    const pesoTotalRow = updatedRows.find(
      (row) => row.label === LABELS_TABLE_CALCINATION.PESO_CALCINADO
    );
    const pesoMuestraRow = updatedRows.find(
      (row) => row.label === LABELS_TABLE_CALCINATION.PESO_CALCINADO_MUESTRA
    );

    if (taraRow && pesoTotalRow && pesoMuestraRow) {
      let totalPesoCalc = 0;

      for (let i = 1; i <= count; i++) {
        const pesoTotal = Number(pesoTotalRow[`capsule_${i}`]) || 0;
        const pesoTara = Number(taraRow[`capsule_${i}`]) || 0;
        const pesoMuestra = (pesoTotal - pesoTara).toFixed(2);

        pesoMuestraRow[`capsule_${i}`] = pesoMuestra;
        totalPesoCalc += Number(pesoMuestra);
      }

      pesoMuestraRow.total = totalPesoCalc.toFixed(2);
    }

    setValue("rowsCurvaCalcination", updatedRows);

    setTimeout(() => {
      const keys = Object.keys(inputRef.current);

      const currentFilter = Object.keys(newRow).filter((key) =>
        key.startsWith("capsule_")
      );

      const firstEmptyInput = currentFilter.find((key) => newRow[key] === "");

      if (firstEmptyInput) {
        const firstEmptyInputKey = keys.find(
          (key) => key === `${newRow.id}_${firstEmptyInput}`
        );

        if (firstEmptyInputKey && inputRef.current[firstEmptyInputKey]) {
          setTimeout(() => inputRef.current[firstEmptyInputKey]?.focus(), 0);
        }
      } else if (newRow.label === LABELS_TABLE_CALCINATION.TARA) {
        const nextRowId = "calcinacion_2";
        const firstInputKey = `${nextRowId}_capsule_1`;

        if (inputRef.current[firstInputKey]) {
          setTimeout(() => inputRef.current[firstInputKey]?.focus(), 0);
        }
      }
    }, 0);

    return newRow;
  };

  const totalPesoCalcinadoMuestra = useMemo(() => {
    const row = rowsCurva.find(
      (row) => row.label === LABELS_TABLE_CALCINATION.PESO_CALCINADO_MUESTRA
    );
    const value = row ? Number(row.total) : 0;
    //setCalcinationValue(value);
    return value;
  }, [rowsCurva]);

  useEffect(() => {
    setCalcinationValue(totalPesoCalcinadoMuestra);
  }, [totalPesoCalcinadoMuestra, setCalcinationValue]);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <CustomDataGrid
              key={`calcination${JSON.stringify(rowsCurva)}`}
              rows={rowsCurva || []}
              columns={columnsCalcinationCurva}
              search={false}
              processRowUpdate={processRowUpdate}
              disablePagination={true}
              footerContent={
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    fontWeight: "bold",
                    gap: 2,
                  }}
                >
                  <Typography variant="sectionTitle">
                    Peso calcinado todas las muestras:
                  </Typography>
                  <Typography variant="sectionTitle">
                    {totalPesoCalcinadoMuestra.toFixed(2)}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TableCalcinationForm;
